// Voice command navigation — Phase 5 polish.
//
// Tap 🗣 → speak a command in pt-BR → app navigates. Hands-free control
// over rooms / variants / Antes-Depois / sun time / narration / QR. Most
// useful in VR (no controllers needed) and on phones one-handed.
//
// Builds on src/ui/voiceInput.ts — reuses the same Web Speech API
// wrapper, just interprets the transcript as a command rather than
// piping it to a textarea.
//
// Intent matching is fuzzy keyword-based — no LLM call needed for the
// 6-12 commands the demo supports. Falls through with a "não entendi"
// toast on unrecognized input so the operator learns the vocabulary.

import type { Hotspot, RoomsConfig } from '../app/types';
import { sunPresetHour, type SunPresetId } from '../app/config';
import { VoiceInput } from './voiceInput';
import { CloudVoiceInput } from './cloudVoiceInput';
import { showToast } from './toast';
import { INFO_INTENT, matchesInfoHotspot, matchesRoom, matchesVariant } from './voiceMatch';
import { matchMenuCommand, type MenuActivation } from './voiceMenu';
import type { MenuItem } from './menu/types';

export interface VoiceCommandsDeps {
  config: RoomsConfig;
  navigateToRoom: (roomId: string) => void;
  setVariant: (variantId: string) => void;
  toggleBeforeAfter: () => void;
  toggleNarrator: () => void;
  showQr: () => void;
  setSunHour: (hour: number) => void;
  /** "Entrar no apartamento" — step from the current room into the unit
   *  (follows the current room's first navigation hotspot, e.g. building
   *  hall → front door). */
  enterApartment: () => void;
  /** Switch the current room to a specific scene: 'antes' (obra), 'depois'
   *  (pronto), or 'projeto' (render). Returns whether a matching variant
   *  exists in the current room. */
  selectScene: (role: 'antes' | 'depois' | 'projeto') => boolean;
  /** Hotspots of the room the visitor is standing in — the only ones worth
   *  matching an info request against. Live, because the room changes. */
  currentHotspots: () => readonly Hotspot[];
  /** Open an info hotspot. In VR this paints the 3D card (HTML modals are not
   *  composited into an immersive session); elsewhere it opens the modal. */
  openInfo: (hotspot: Extract<Hotspot, { type: 'info' }>) => void;
  /**
   * Fire a menu entry by voice, reporting which of the three things happened:
   * it ran, it is a DOM-only control the headset cannot show, or nothing in
   * this document can act on it. Saying which beats appearing to have worked.
   *
   * This is what substitutes for the menu in VR, where none is rendered.
   */
  activateMenu: (item: MenuItem) => MenuActivation;
  /** The speakable menu index, both languages. Built once by App. */
  menuIndex: () => readonly { text: string; item: MenuItem }[];
}

export class VoiceCommands {
  private readonly deps: VoiceCommandsDeps;
  private readonly voice = new VoiceInput();
  /**
   * Fallback recogniser for browsers with no Web Speech API — which means
   * every iPhone. Safari ships speechSynthesis (so the narrator works) but not
   * SpeechRecognition, so the button used to hide itself on the single most
   * common device this tour is opened on.
   *
   * CloudVoiceInput records with MediaRecorder and transcribes server-side, and
   * iOS has supported both getUserMedia and MediaRecorder for years. It was
   * already built and wired — but only into the VR grip-to-talk path, where a
   * Quest headset can also do native recognition anyway.
   *
   * Created lazily: constructing it does not touch the mic, but there is no
   * reason to hold one on a browser that will never use it.
   */
  private cloud: CloudVoiceInput | null = null;
  private button: HTMLButtonElement | null = null;
  private listening = false;

  constructor(deps: VoiceCommandsDeps) {
    this.deps = deps;
  }

  install(): void {
    // Hide only when NEITHER recogniser is available. The registry runs the same
    // predicate (capabilities.ts hasVoiceInput) so the entry never takes a rail
    // slot it cannot use; this stays as the belt to that braces.
    if (!VoiceCommands.isSupported()) {
      const btn = document.getElementById('voice-command-button');
      if (btn) btn.style.display = 'none';
      return;
    }
    if (!VoiceInput.isSupported()) this.cloud = new CloudVoiceInput();
    this.button = document.getElementById('voice-command-button') as HTMLButtonElement | null;
    if (!this.button) return;
    this.button.addEventListener('click', () => void this.listen());
  }

  /** Either recogniser will do — native first, mic-and-server second. */
  static isSupported(): boolean {
    return VoiceInput.isSupported() || CloudVoiceInput.isSupported();
  }

  async listen(): Promise<void> {
    if (this.listening) {
      // Cloud mode is press-to-start / press-to-stop: the second tap is what
      // ends the recording and returns the transcript, so it must not cancel.
      if (this.cloud) return;
      this.voice.cancel();
      return;
    }
    this.listening = true;
    this.renderListening(true);
    try {
      const transcript = this.cloud ? await this.listenViaCloud() : await this.voice.listen('pt-BR');
      if (transcript) {
        const handled = this.dispatch(transcript);
        if (!handled) {
          showToast(`Não entendi: "${transcript}". Exemplos: "ir para a cozinha", "dark premium", "antes e depois"`, { tone: 'info' });
        }
      } else {
        showToast('Não captei áudio — tente de novo', { tone: 'info' });
      }
    } finally {
      this.listening = false;
      this.renderListening(false);
    }
  }

  /**
   * Interpret a transcript as a command and act on it. Returns true if a
   * command matched. Public so the VR push-to-talk path (App, via Groq
   * Whisper) can reuse the same intent matching as the 🗣 button.
   */
  dispatch(transcript: string): boolean {
    const text = transcript.toLowerCase().trim();

    // "Entrar no apartamento" — step from the building hall into the unit.
    // Checked before room matching so the room named "Entrada" doesn't grab it.
    if (
      /\bentrar no ap/.test(text) ||
      /\bentra(?:r|m)?\b[\s\S]*\b(apartamento|ap[eê]|unidade|im[oó]vel|casa)\b/.test(text) ||
      /\b(go|come|step|walk)\s+(in|into|inside)\b/.test(text) ||
      /\benter\s+(the\s+)?(apartment|unit|flat|home|house)\b/.test(text)
    ) {
      this.deps.enterApartment();
      showToast('Comando: entrar no apartamento', { tone: 'info' });
      return true;
    }

    // Menu entries, but ONLY with an explicit "operate this" verb ("abrir a
    // planta", "turn on sound"). Runs before rooms because the verb makes it
    // unambiguous; the bare-label pass runs last, after rooms have declined.
    {
      const item = matchMenuCommand(text, this.deps.menuIndex(), { requireVerb: true });
      if (item && this.runMenuItem(item)) return true;
    }

    // Info hotspots in the room the visitor is standing in. Before variants and
    // rooms so "me fale sobre a cozinha americana" (a hotspot) is not swallowed
    // by the room called Cozinha.
    {
      const handled = this.tryInfoIntent(text);
      if (handled !== null) return handled;
    }

    // Explicit A/B toggle phrases flip between before & after.
    if (/\b(antes e depois|compara(r|ção|cao)|alternar|toggle|before and after|before ?\/ ?after|compare)\b/.test(text)) {
      this.deps.toggleBeforeAfter();
      showToast('Comando: Antes / Depois', { tone: 'info' });
      return true;
    }

    // Directional scene switch — jump straight to a specific variant.
    // Checked before room/variant scans so "depois"/"projeto" aren't misread.
    {
      let role: 'antes' | 'depois' | 'projeto' | null = null;
      if (/\b(projeto|render(izado)?|projetado|rendering|rendered)\b/.test(text)) role = 'projeto';
      else if (/\b(depois|pronto|limpo|finalizado|acabado|reformado|after|finished|renovated|staged)\b/.test(text)) role = 'depois';
      else if (/\b(antes|original|obra|atual|before|current|as ?is|empty)\b/.test(text)) role = 'antes';
      if (role) {
        const applied = this.deps.selectScene(role);
        showToast(applied ? `Comando: cena "${role}"` : `Sem cena "${role}" neste ambiente`, {
          tone: applied ? 'info' : 'error',
        });
        return true; // intent recognized even if this room has no such scene
      }
    }

    // Tour narrator toggle.
    if (/\b(narrar|narração|tour narrado|narration|narrate|audio guide|guia de áudio|read it|read this)\b/.test(text)) {
      this.deps.toggleNarrator();
      showToast(`Comando: tour narrado`, { tone: 'info' });
      return true;
    }

    // QR code.
    if (/\b(qr|qr ?code|código qr|escanear|scan|share|compartilhar)\b/.test(text)) {
      this.deps.showQr();
      showToast(`Comando: QR code`, { tone: 'info' });
      return true;
    }

    // Sun time-of-day intents.
    const sunHour = parseSunIntent(text);
    if (sunHour !== null) {
      this.deps.setSunHour(sunHour);
      showToast(`Comando: ${formatHour(sunHour)}`, { tone: 'info' });
      return true;
    }

    // Variant: scan all variant names + the canonical id stems.
    for (const room of this.deps.config.rooms) {
      for (const variant of room.variants) {
        if (matchesVariant(text, variant.name, variant.id)) {
          this.deps.setVariant(variant.id);
          showToast(`Comando: variante "${variant.name}"`, { tone: 'info' });
          return true;
        }
      }
    }

    // Room navigation — after variants (so a "dark sala" doesn't grab sala over
    // the variant) and before the bare-label menu pass (so a room actually
    // named "Planta" or "Galeria" still wins its own name).
    const roomMatch = matchesRoom(text, this.deps.config.rooms);
    if (roomMatch) {
      this.deps.navigateToRoom(roomMatch.id);
      showToast(`Comando: ${roomMatch.name}`, { tone: 'info' });
      return true;
    }

    // Menu entries by bare label, with no verb — the last thing tried, because
    // a single word like "galeria" is the weakest signal in the vocabulary.
    {
      const item = matchMenuCommand(text, this.deps.menuIndex());
      if (item && this.runMenuItem(item)) return true;
    }

    return false;
  }

  /**
   * Fire a menu entry and report it. Returns false when the registry knows the
   * entry but nothing can act on it — an operator-only tool on a public tour,
   * or a legacy entry whose button is not in this DOM. Saying so beats
   * appearing to work, which is the failure this whole path exists to avoid.
   */
  private runMenuItem(item: MenuItem): boolean {
    const result = this.deps.activateMenu(item);
    if (result === 'not-in-vr') {
      showToast('Esse painel não aparece no visor — saia do VR para usá-lo', { tone: 'error' });
    } else if (result === 'no-control') {
      showToast('Esse controle não está disponível aqui', { tone: 'error' });
    }
    // Every branch is "handled": the visitor was understood and answered.
    // Returning false would fall through to room matching and could teleport
    // them somewhere as a side effect of asking for a control.
    return true;
  }

  /**
   * Handle an info request against the current room.
   *
   * Returns true when it acted, false when it recognised an info intent it
   * could not satisfy (and has already said so), and **null** when this was not
   * an info request at all — which is the caller's signal to keep matching.
   * A plain boolean could not express "not mine", and returning false for that
   * case would have made every non-info command fall out of the dispatcher.
   */
  private tryInfoIntent(text: string): boolean | null {
    const hotspots = this.deps.currentHotspots();
    const named = matchesInfoHotspot(text, hotspots);
    if (named) {
      this.deps.openInfo(named);
      showToast(`Comando: ${named.label}`, { tone: 'info' });
      return true;
    }
    if (!INFO_INTENT.test(text)) return null;

    // An info intent with no name attached. One info hotspot in the room is
    // unambiguous — open it. Several, and naming them teaches the vocabulary,
    // which is the only discovery channel in a headset where the labels are
    // not drawn.
    const infos = hotspots.filter(
      (h): h is Extract<Hotspot, { type: 'info' }> => h.type === 'info',
    );
    if (infos.length === 1 && infos[0]) {
      this.deps.openInfo(infos[0]);
      showToast(`Comando: ${infos[0].label}`, { tone: 'info' });
      return true;
    }
    if (infos.length === 0) {
      showToast('Nada a detalhar neste ambiente', { tone: 'info' });
      return false;
    }
    showToast(`Sobre o quê? ${infos.map((h) => h.label).join(', ')}`, { tone: 'info' });
    return false;
  }

  /**
   * Record until the user taps again, then transcribe.
   *
   * Unlike the native recogniser there is no end-of-speech detection, so the
   * second tap is the only stop signal — which is why listen() must not treat
   * it as a cancel. A failed mic permission resolves null and the caller shows
   * the same "no audio" toast as an empty native result.
   */
  private async listenViaCloud(): Promise<string | null> {
    const cloud = this.cloud;
    if (!cloud) return null;
    const started = await cloud.start();
    if (!started) {
      showToast('Não consegui acessar o microfone — verifique a permissão', { tone: 'error' });
      return null;
    }
    await new Promise<void>((resolve) => {
      const stop = () => {
        this.button?.removeEventListener('click', stop);
        resolve();
      };
      this.button?.addEventListener('click', stop, { once: true });
    });
    return cloud.stop();
  }

  private renderListening(on: boolean): void {
    if (!this.button) return;
    const icon = this.button.querySelector('.icon-button__icon') as HTMLElement | null;
    if (icon) icon.textContent = on ? '⏺️' : '🗣️';
    this.button.classList.toggle('is-active', on);
    this.button.setAttribute('aria-pressed', String(on));
  }
}

// ── Intent matchers ──────────────────────────────────────────────────

// Keyword → preset id. Order matters: "meia noite" must be tested before "noite",
// and "hora dourada" before the bare hour matcher below. The HOURS come from
// SUN_PRESETS in config.ts so the sun panel and the in-VR time submenu resolve
// the same instant for the same preset. In Phase 6 the keyword lists move into
// the i18n catalogue so each locale ships its own without touching this matcher.
const SUN_INTENT_PATTERNS: ReadonlyArray<readonly [RegExp, SunPresetId]> = [
  [/\b(meia ?noite|midnight)\b/, 'midnight'],
  [/\b(amanhecer|dawn|nascer do sol|sunrise)\b/, 'dawn'],
  [/\b(manhã|morning|de manhã)\b/, 'morning'],
  [/\b(meio[\- ]?dia|noon|hora do almoço|almoço)\b/, 'noon'],
  [/\b(tarde|afternoon|à tarde|de tarde)\b/, 'afternoon'],
  [/\b(hora dourada|golden hour|por do sol|pôr do sol|sunset)\b/, 'goldenHour'],
  [/\b(crepúsculo|crepusculo|dusk|anoitecer)\b/, 'dusk'],
  [/\b(noite|night|à noite|de noite)\b/, 'night'],
];

function parseSunIntent(text: string): number | null {
  for (const [pattern, id] of SUN_INTENT_PATTERNS) {
    if (pattern.test(text)) return sunPresetHour(id);
  }
  // Explicit hours: "às 14 horas", "8 da manhã", etc.
  const m = text.match(/\b(\d{1,2})(?:[: ]?(\d{2}))?\s*(?:horas?|h)?\b/);
  if (m) {
    const h = parseInt(m[1] ?? '0', 10);
    const min = m[2] ? parseInt(m[2], 10) / 60 : 0;
    if (h >= 0 && h <= 24) return h + min;
  }
  return null;
}

function formatHour(h: number): string {
  const hh = Math.floor(h).toString().padStart(2, '0');
  const mm = Math.round((h - Math.floor(h)) * 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}
